import {
  CANVAS_EDIT_MODEL,
  CANVAS_EDIT_QUALITY,
  awaitRun,
  detectCanvasText,
  estimateCredits,
  startCanvasEdit,
  type PictureModel,
} from '../../shared/services/pictureService'
import { uploadProjectFile } from '../../shared/services/projectFileService'
import { resolveImages } from '../../shared/services/imageRefs'
import {
  compose,
  cropped,
  fileOf,
  fit,
  loadBitmap,
  planText,
  prepareEdit,
  scaledCopy,
  type ComposeRecipe,
  type Rect,
  type TextLine,
} from './pictureOps'

/**
 * Edit text, away from the board (Huanrui, 2026-10-07)
 * -------------------------------------------------------------
 * The Edit Text page on the website and the Edit Text view in the Miro panel
 * do what the canvas card does (textNode.tsx, ProjectCanvas), on a picture
 * that is not on a board: read the lines of text, then redraw the changed
 * ones with Image 2.5 Sunburst and let only those through over the original.
 * The same server calls, the same planning (planText), the same lining-up
 * and putting-together (fit, compose).
 *
 * The pictures sent travel through the person's own hidden project, as the
 * generate and upscale pages' do, because a run reads its pictures from a
 * project. The page and the panel keep no history of their own.
 */

/** At most this many parts in one Apply; each part is one Sunburst run. */
export const MAX_TEXT_PARTS = 6

/** The lines on a picture, or on the area of it given (fractions), placed on the whole picture. */
export async function readTextOn(
  projectId: string,
  picture: HTMLCanvasElement,
  area: Rect | null,
): Promise<{ lines: TextLine[]; credits: number }> {
  const pw = picture.width
  const ph = picture.height
  const part = area ? { x: area.x * pw, y: area.y * ph, w: area.w * pw, h: area.h * ph } : { x: 0, y: 0, w: pw, h: ph }
  const piece = area ? cropped(picture, part) : picture
  const small = scaledCopy(piece, 2048)
  const stored = await uploadProjectFile(projectId, await fileOf(small, 'text-read', false), {
    width: small.width,
    height: small.height,
  })
  const found = await detectCanvasText({ projectId, image: { store: 'project', id: stored.id } })
  return {
    credits: found.credits,
    lines: found.lines.map((line) => ({
      text: line.text,
      to: line.text,
      box: {
        x: (part.x + line.box.x * part.w) / pw,
        y: (part.y + line.box.y * part.h) / ph,
        w: (line.box.w * part.w) / pw,
        h: (line.box.h * part.h) / ph,
      },
    })),
  }
}

/** How many runs Apply would start, and about what they cost. */
export function textEstimate(
  lines: TextLine[],
  size: { width: number; height: number },
  together: boolean,
  model: PictureModel | undefined,
): { parts: number; credits: number } {
  const changed = lines.filter((line) => line.to.trim() !== line.text.trim()).length
  if (changed === 0) return { parts: 0, credits: 0 }
  const parts = together ? 1 : size.width && size.height ? planText(lines, size.width, size.height).length : changed
  const each = model ? estimateCredits(model, CANVAS_EDIT_QUALITY, 'auto', 1, 1) : 0
  return { parts, credits: parts * each }
}

export interface TextApplied {
  /** The whole picture with the changed lines redrawn. */
  canvas: HTMLCanvasElement
  credits: number
  /** Parts redrawn, and parts that were not (with the first reason). */
  made: number
  missed: number
  error: string
  /** "A → B; C → D", for a title or a record. */
  said: string
}

/**
 * Apply: one Sunburst run per part (planText; one part in all when `together`,
 * which is what an Area means), waited on together, and put back over the
 * original. Throws only when nothing at all could be redrawn.
 */
export async function applyTextOn(ask: {
  projectId: string
  picture: HTMLCanvasElement
  png: boolean
  title: string
  lines: TextLine[]
  together: boolean
  onLive?: (live: number, limit: number) => void
}): Promise<TextApplied> {
  const { projectId, picture, png, title, lines, together } = ask
  const groups = planText(lines, picture.width, picture.height, together)
  if (groups.length === 0) throw new Error('Change at least one line')
  if (groups.length > MAX_TEXT_PARTS) {
    throw new Error(`The changes are spread over ${groups.length} parts of the picture — ${MAX_TEXT_PARTS} at most in one go`)
  }
  const said = lines
    .filter((line) => line.to.trim() !== line.text.trim())
    .map((line) => `${line.text.trim()} → ${line.to.trim() || '(removed)'}`)
    .join('; ')

  // Every part started first, so they run side by side.
  const started: { runId: string; recipe: ComposeRecipe }[] = []
  let missed = 0
  let error = ''
  for (const group of groups) {
    try {
      const prepared = prepareEdit(picture, group.box, { store: 'project', id: '' }, 0, png, title)
      const recipe: ComposeRecipe = { ...prepared.recipe, boxes: group.boxes, feather: group.feather }
      const sent = await uploadProjectFile(projectId, await fileOf(prepared.request, 'edit-input', true), {
        width: prepared.request.width,
        height: prepared.request.height,
      })
      const began = await startCanvasEdit({
        projectId,
        kind: 'text',
        image: { store: 'project', id: sent.id },
        references: [],
        size: prepared.size,
        prompt: '',
        changes: group.changes,
        keep: group.keep,
      })
      ask.onLive?.(began.live, began.limit)
      started.push({ runId: began.runId, recipe })
    } catch (caught) {
      missed += 1
      error ||= caught instanceof Error ? caught.message : 'It did not start'
    }
  }
  if (started.length === 0) throw new Error(error || 'It did not start')

  const runs = await Promise.all(started.map((part) => awaitRun(part.runId).catch(() => null)))
  const credits = runs.reduce((sum, run) => sum + (run?.credits ?? 0), 0)
  const last = runs.find((run) => run?.limit)
  if (last) ask.onLive?.(last.live, last.limit)

  let out = picture
  let made = 0
  for (const [index, part] of started.entries()) {
    const run = runs[index]
    const drawn = run?.status === 'succeeded' ? run.pictures[0] : undefined
    if (!drawn) {
      missed += 1
      error ||= run?.error || 'A part did not finish'
      continue
    }
    const url = drawn.originalPath || (await resolveImages([{ store: drawn.store, id: drawn.id }]))[0]?.originalPath
    if (!url) {
      missed += 1
      error ||= 'A part could not be read back'
      continue
    }
    const { bitmap } = await loadBitmap(url)
    const fitted = fit(picture, bitmap, part.recipe)
    bitmap.close()
    out = compose(out, fitted, part.recipe)
    made += 1
  }
  if (made === 0) throw new Error(error || 'Nothing came back')
  return { canvas: out, credits, made, missed, error, said }
}

/** The model the page and panel quote with. */
export const findSunburst = (models: PictureModel[]) => models.find((one) => one.slug === CANVAS_EDIT_MODEL)
